// Application configuration from environment variables and runtime config
// Runtime config (from window.__APP_CONFIG__) takes precedence over build-time env vars
// This allows changing the API URL on the server without rebuilding

/**
 * Get runtime configuration from window object (set in index.html)
 * This allows server-side configuration without rebuilding
 */
const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  return {};
};

/**
 * Get the API base URL from runtime config, environment variables, or default
 * Priority: window.__APP_CONFIG__.apiBaseUrl > VITE_API_BASE_URL > default
 */
export const getApiBaseUrl = () => {
  // In dev mode, use relative path so Vite proxy can forward to API (avoids CORS)
  if (import.meta.env.DEV) {
    return '/api';
  }

  const runtimeConfig = getRuntimeConfig();
  
  // Check runtime config first (allows server-side configuration)
  if (runtimeConfig.apiBaseUrl) {
    return runtimeConfig.apiBaseUrl;
  }
  
  // Fall back to build-time environment variable
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Default fallback
  return 'http://lwstaffportal-api.test/api';
};

/**
 * Get the API base URL without the /api suffix (for direct backend web routes)
 */
export const getApiBaseUrlWithoutApi = () => {
  const baseUrl = getApiBaseUrl();
  // Remove /api suffix if present
  return baseUrl.replace(/\/api\/?$/, '');
};

/**
 * Staff KingsChat login: full URL to API web route (GET /staff/login → staffLoginLink)
 */
export const getStaffKingsChatLoginUrl = (redirect = '') => {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const path = '/staff/login';
  if (redirect) {
    return `${base}${path}?redirect=${encodeURIComponent(redirect)}`;
  }
  return `${base}${path}`;
};

/** Staff password recovery: GET /staff/forgot-password → KingsChat → callback issues reset token */
export const getStaffKingsChatForgotPasswordUrl = () => {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return `${base}/staff/forgot-password`;
};

/**
 * Admin KingsChat login: full URL to API web route (GET /admin/login → adminLoginLink)
 */
export const getAdminKingsChatLoginUrl = (redirect = '') => {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const path = '/admin/login';
  if (redirect) {
    return `${base}${path}?redirect=${encodeURIComponent(redirect)}`;
  }
  return `${base}${path}`;
};

// Export constants for convenience (evaluated at module load, may not reflect runtime changes)
// For runtime config, use the functions above instead
export const API_BASE_URL = getApiBaseUrl();
export const API_BASE_URL_WITHOUT_API = getApiBaseUrlWithoutApi();

/** Public CSV download for staff week attire submissions (no auth). */
export const getStaffWeekAttirePublicExportUrl = () => {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return `${base}/staffweek-attire/submissions/export.csv`;
};
